import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import { Spin } from "antd";
import { ROLL_CALL_API_BASE_URL } from "../../RollCallConfig/RollCallConfig";

const CHECKBOXES = [
  { name: "IsNightShift", label: "Night Shift" },
  { name: "DayCross", label: "Day Cross" },
  { name: "IsRotational", label: "Rotational" },
  { name: "IsOtApplicable", label: "OT Applicable" },
  { name: "IsFixedShift", label: "Fixed Shift" },
];

const EditShift = ({ isOpen, onClose, record, onSuccess }) => {
  const [form, setForm] = useState({});
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [loading, setLoading] = useState(false);

  
  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");
    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    }
  }, []);

 
  useEffect(() => {
    if (record && isOpen) {
      setForm({
        Name: record.Name || "",
        Code: record.Code || "",
        StartTime: record.StartTime ? record.StartTime.slice(11, 16) : "", // "HH:mm" from ISO
        EndTime: record.EndTime ? record.EndTime.slice(11, 16) : "",
        StartGrace: record.StartGrace ?? "",
        EndGrace: record.EndGrace ?? "",
        ApproxStartGrace: record.ApproxStartGrace ?? "",
        ApproxEndGrace: record.ApproxEndGrace ?? "",
        WorkMins: record.WorkMins ?? "",
        OTCalcMins: record.OTCalcMins ?? "",
        operdays: record.operdays ?? "",
        IsNightShift: !!record.IsNightShift,
        DayCross: !!record.DayCross,
        IsRotational: !!record.IsRotational,
        IsOtApplicable: !!record.IsOtApplicable,
        IsFixedShift: !!record.IsFixedShift,
      });
    }
  }, [record, isOpen]);

  const resetForm = () => setForm({});

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (
      !form.Name?.trim() ||
      !form.Code?.trim() ||
      !form.StartTime ||
      !form.EndTime
    ) {
      Swal.fire("Warning", "Please fill all required fields.", "warning");
      return;
    }

    if (!record?.Id) {
      Swal.fire("Error", "Invalid record", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        Id: record.Id,
        SuperId: Number(superIdData),
        Name: form.Name.trim(),
        Code: form.Code.trim(),
        StartTime: `${form.StartTime}:00`,
        EndTime: `${form.EndTime}:00`,
        StartGrace: Number(form.StartGrace) || 0,
        EndGrace: Number(form.EndGrace) || 0,
        ApproxStartGrace: Number(form.ApproxStartGrace) || 0,
        ApproxEndGrace: Number(form.ApproxEndGrace) || 0,
        WorkMins: Number(form.WorkMins) || 0,
        OTCalcMins: Number(form.OTCalcMins) || 0,
        operdays: Number(form.operdays) || 0,
        IsNightShift: form.IsNightShift ? 1 : 0,
        DayCross: form.DayCross ? 1 : 0,
        IsRotational: form.IsRotational ? 1 : 0,
        IsOtApplicable: form.IsOtApplicable ? 1 : 0,
        IsFixedShift: form.IsFixedShift ? 1 : 0,
      };

      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/shifts/updateShift`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (data.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Shift updated successfully!",
          timer: 1500,
          showConfirmButton: false,
        }).then(() => {
          onClose();
          if (onSuccess) onSuccess();
        });
      } else {
        Swal.fire("Error", data.message || "Update failed", "error");
      }
    } catch (error) {
      console.error("Error updating shift:", error);
      Swal.fire("Error", "Something went wrong while updating.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleCancel();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  if (!isOpen || !record) return null;

  return (
    <>
      <style>{`
        @media (max-width: 576px) { #offcanvasRightEditShift { width: 90% !important; } }
        @media (min-width: 577px) and (max-width: 992px) { #offcanvasRightEditShift { width: 60% !important; } }
        @media (min-width: 993px) and (max-width: 1440px) { #offcanvasRightEditShift { width: 50% !important; } }
        @media (min-width: 1441px) { #offcanvasRightEditShift { width: 35% !important; } }

        .shift-flag-box .form-check-input:checked {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
      `}</style>

      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255,255,255,0.2)",
          }}
        >
          <Spin size="large" tip="Updating..." />
        </div>
      )}

      <div className="offcanvas-backdrop fade show" onClick={handleCancel} />

      <div
        className="offcanvas offcanvas-end show"
        id="offcanvasRightEditShift"
        style={{ visibility: "visible" }}
      >
        <div className="offcanvas-header">
          <h4>Edit Shift</h4>
          <button
            type="button"
            className="btn-close text-reset"
            onClick={handleCancel}
            aria-label="Close"
          />
        </div>
        <hr className="m-0 p-0" />

        <div className="offcanvas-body">
          <form onSubmit={handleSave}>
            <div className="row">
              <div className="col-4 mb-2">
                <label className="form-label">
                  <span className="text-danger">*</span> Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="Name"
                  value={form.Name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-4 mb-2">
                <label className="form-label">
                  <span className="text-danger">*</span> Code
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="Code"
                  value={form.Code}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-4 mb-2">
                <label className="form-label">
                  <span className="text-danger">*</span> Start Time
                </label>
                <input
                  type="time"
                  className="form-control"
                  name="StartTime"
                  value={form.StartTime}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-4 mb-2">
                <label className="form-label">
                  <span className="text-danger">*</span> End Time
                </label>
                <input
                  type="time"
                  className="form-control"
                  name="EndTime"
                  value={form.EndTime}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-4 mb-2">
                <label className="form-label">
                  <span className="text-danger">*</span> Operating Days (1–7)
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="operdays"
                  value={form.operdays}
                  onChange={handleChange}
                  min={1}
                  max={7}
                  required
                />
              </div>

              <div className="col-4 mb-2">
                <label className="form-label">Work Minutes</label>
                <input
                  type="number"
                  className="form-control"
                  name="WorkMins"
                  value={form.WorkMins}
                  onChange={handleChange}
                />
              </div>

              <div className="col-4 mb-2">
                <label className="form-label">OT Calc Minutes</label>
                <input
                  type="number"
                  className="form-control"
                  name="OTCalcMins"
                  value={form.OTCalcMins}
                  onChange={handleChange}
                />
              </div>

              <div className="col-4 mb-2">
                <label className="form-label">Start Grace (mins)</label>
                <input
                  type="number"
                  className="form-control"
                  name="StartGrace"
                  value={form.StartGrace}
                  onChange={handleChange}
                />
              </div>

              <div className="col-4 mb-2">
                <label className="form-label">End Grace (mins)</label>
                <input
                  type="number"
                  className="form-control"
                  name="EndGrace"
                  value={form.EndGrace}
                  onChange={handleChange}
                />
              </div>

              <div className="col-4 mb-2">
                <label className="form-label">Approx Start Grace (mins)</label>
                <input
                  type="number"
                  className="form-control"
                  name="ApproxStartGrace"
                  value={form.ApproxStartGrace}
                  onChange={handleChange}
                />
              </div>

              <div className="col-4 mb-2">
                <label className="form-label">Approx End Grace (mins)</label>
                <input
                  type="number"
                  className="form-control"
                  name="ApproxEndGrace"
                  value={form.ApproxEndGrace}
                  onChange={handleChange}
                />
              </div>

              <div className="col-12 mb-2">
                <div className="shift-flag-box rounded border border-secondary-subtle bg-light px-3 pt-2 pb-1">
                  <div className="row">
                    {CHECKBOXES.map(({ name, label }) => (
                      <div className="col-4 mb-2" key={name}>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="checkbox"
                            className="form-check-input m-0"
                            id={name}
                            name={name}
                            checked={form[name] || false}
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label mb-0"
                            htmlFor={name}
                            style={{ cursor: "pointer", fontSize: 13 }}
                          >
                            {label}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-warning me-2"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={loading}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

EditShift.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  record: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default EditShift;
